import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { MatCalendarCellClassFunction } from "@angular/material/datepicker";
import { Chart } from "angular-highcharts";
import { SeriesOptionsType, YAxisOptions } from "highcharts";
import { EventService } from "../../services/event.service";
import * as moment from "moment";
import { Router } from "@angular/router";
import { HvService } from "../../services/hv.service";

interface RawDataPalyload {
  testId: string;
  month: number;
  year: number;
  params: {
    g1?: string;
    g2?: string;
    g3?: string;
  };
}

interface RawData {
  g1: [number, number][];
  g2: [number, number][];
  g3: [number, number][];
}

@Component({
  selector: "app-exponent-eva",
  templateUrl: "./exponent-eva.component.html",
  styleUrls: ["./exponent-eva.component.css"],
  encapsulation: ViewEncapsulation.None,
})
export class ExponentEvaComponent implements OnInit {
  constructor(
    private eventService: EventService,
    private router: Router,
    private hvService: HvService
  ) {}
  chart: Chart;
  testslist: string[] = [];
  masterParametersList: string[] = [];
  redLineParameterList: string[] = this.masterParametersList;
  blueLineParameterList: string[] = this.masterParametersList;
  greenLineParameterList: string[] = this.masterParametersList;
  redLineParameter: string = "";
  blueLineParameter: string = "";
  greenLineParameter: string = "";
  selectedTest: string = this.testslist[0];
  rawData: RawData = {
    g1: [],
    g2: [],
    g3: [],
  };
  defaultRawDataPayload: RawDataPalyload = {
    testId: "EOL_400001000_240719_060432",
    month: 7,
    year: 2024,
    params: {
      g1: this.redLineParameter,
      g2: this.blueLineParameter,
      g3: this.greenLineParameter,
    },
  };
  selectedDate = new Date();
  datesToHighlight: any[] = [];
  isTestDatesReceived: boolean = false;
  isTestSelected: boolean = false;
  isRawDataReceived: boolean = false;
  batteryType: "piccolo" | "tg" | "hv" = "tg";
  localRawData = this.parseLocalStoragePayload();

  ngOnInit(): void {
    this.getEpackEolTestDates();

    if (this.localRawData) {
      const { testDate, testId } = this.localRawData;
      this.getEpackEolTestsByDate(testDate);
      this.selectedDate = testDate;
      this.selectedTest = testId;

      this.getEpackEolRawParams();

      // remove local raw data once graph is plotted
      localStorage.removeItem("epackRawDataInfo");
    } else {
      this.getEpackEolTestsByDate(this.selectedDate);
    }
  }

  parseLocalStoragePayload(): {
    testId: string;
    testDate: Date;
  } {
    if (localStorage.getItem("epackRawDataInfo")) {
      const payload: {
        testId: string;
        testDate: string;
      } = JSON.parse(localStorage.getItem("epackRawDataInfo"));

      const parsedDate = new Date(
        payload.testDate.split("-").reverse().join("-")
      );

      return {
        testId: payload.testId,
        testDate: parsedDate,
      };
    } else {
      return null;
    }
  }

  udpateParamterList(linetype: "red" | "blue" | "green", event: KeyboardEvent) {
    const keyword = (event.target as HTMLInputElement).value.toLowerCase();

    if (linetype === "red") {
      this.redLineParameterList = this.masterParametersList.filter(
        (currentParameter) => currentParameter.toLowerCase().includes(keyword)
      );
    } else if (linetype === "blue") {
      this.blueLineParameterList = this.masterParametersList.filter(
        (currentParameter) => currentParameter.toLowerCase().includes(keyword)
      );
    } else {
      this.greenLineParameterList = this.masterParametersList.filter(
        (currentParameter) => currentParameter.toLowerCase().includes(keyword)
      );
    }
  }

  getEpackEolRawData() {
    const payload: RawDataPalyload = {
      testId: this.selectedTest,
      month: this.selectedDate.getMonth() + 1,
      year: Number(moment(this.selectedDate).format("YYYY")),
      params: {
        g1: this.redLineParameter,
        g2: this.blueLineParameter,
        g3: this.greenLineParameter,
      },
    };

    this.isRawDataReceived = false;
    this.isTestSelected = this.selectedTest.length > 0 ? true : false;

    if (this.batteryType === "tg") {
      this.eventService.getEpackEolRawData(payload).subscribe(
        (res: { success: boolean; data: RawData }) => {
          if (res.success) {
            this.rawData = res.data;
            this.isRawDataReceived = true;
            this.plotChart(this.rawData);
          }
        },
        (error) => {
          console.log("error getting Raw data: ", error);
        }
      );
    } else if (this.batteryType === "piccolo") {
      this.eventService.getPicoloEpackEolRawData(payload).subscribe(
        (res: { success: boolean; data: RawData }) => {
          if (res.success) {
            this.rawData = res.data;
            this.isRawDataReceived = true;
            this.plotChart(this.rawData);
          }
        },
        (error) => {
          console.log("error getting Raw data: ", error);
        }
      );
    } else if (this.batteryType === "hv") {
      this.hvService.getHvEpackEolRawData(payload).subscribe(
        (res: { success: boolean; data: RawData }) => {
          if (res.success) {
            this.rawData = res.data;
            this.isRawDataReceived = true;
            this.plotChart(this.rawData);
          }
        },
        (error) => {
          console.log("error getting Raw data: ", error);
        }
      );
    }
  }

  plotChart(chartData: RawData) {
    const { g1, g2, g3 } = chartData;
    const series: SeriesOptionsType[] = [];
    const yAxis: YAxisOptions[] = Array(3).fill({
      title: {
        text: "",
      },
    });

    const config = {
      g1: {
        color: "#eb262d",
        title: this.redLineParameter,
      },
      g2: {
        color: "#34495e",
        title: this.blueLineParameter,
      },
      g3: {
        color: "#16a085",
        title: this.greenLineParameter,
      },
    };

    if (g1.length) {
      series.push({
        type: "spline",
        color: config.g1.color,
        name: config.g1.title,
        data: chartData.g1,
        yAxis: 0,
      });

      yAxis[0] = {
        title: {
          text: config.g1.title,
          style: {
            color: config.g1.color,
            fontWeight: "bold",
          },
        },
        labels: {
          style: {
            color: config.g1.color,
          },
          align: "right",
        },
      };
    }

    if (g2.length) {
      series.push({
        type: "spline",
        color: config.g2.color,
        name: config.g2.title,
        data: chartData.g2,
        yAxis: 1,
      });

      yAxis[1] = {
        title: {
          text: config.g2.title,
          style: {
            color: config.g2.color,
            fontWeight: "bold",
          },
          x: -10,
        },
        opposite: true,
        labels: {
          style: {
            color: config.g2.color,
          },
          x: -10,
        },
      };
    }

    if (g3.length) {
      series.push({
        type: "spline",
        color: config.g3.color,
        name: config.g3.title,
        data: chartData.g3,
        yAxis: 2,
      });

      yAxis[2] = {
        title: {
          text: config.g3.title,
          style: {
            color: config.g3.color,
            fontWeight: "bold",
          },
          x: 10,
        },
        opposite: true,
        labels: {
          style: {
            color: config.g3.color,
          },
          align: "right",
        },
      };
    }

    this.chart = new Chart({
      chart: {
        type: "spline",
        zoomType: "x",
        resetZoomButton: {
          position: {
            align: "right",
            verticalAlign: "top",
            x: -10,
            y: -50,
          },
        },
      },
      title: {
        text: "Exponent EVA",
        style: {
          top: "-5px",
        },
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      xAxis: [
        {
          minRange: 100,
          type: "datetime",
          crosshair: true,
          title: {
            text: "Time",
            style: {
              color: "#34495e",
              fontWeight: "bold",
            },
          },
        },
      ],
      yAxis,
      tooltip: {
        xDateFormat: "%e %b,%Y @ %H:%M:%S",
        shared: true,
      },
      time: {
        timezoneOffset: -330, // UTC -5:30
      },
      series,
    });
  }

  highlightDates(): MatCalendarCellClassFunction<Date> {
    return (cellDate, view) => {
      // Only highligh dates inside the month view.
      if (view === "month") {
        const date = cellDate.getDate();

        const validHighlight = this.datesToHighlight
          .map((stringDate: string) => new Date(stringDate))
          .some(
            (genereatedDate: Date) =>
              genereatedDate.getDate() === date &&
              genereatedDate.getMonth() === cellDate.getMonth() &&
              genereatedDate.getFullYear() === cellDate.getFullYear()
          );

        return validHighlight ? "special-date" : "";
      }

      return "";
    };
  }

  dateChange(selectedDate: Date) {
    this.testslist = [];
    this.masterParametersList = [];
    this.selectedTest = "";
    this.isTestSelected = false;
    this.isRawDataReceived = false;

    this.resetParametersList();
    this.getEpackEolTestsByDate(selectedDate);
  }

  getEpackEolTestDates() {
    this.datesToHighlight = [];
    this.isTestDatesReceived = false;

    this.eventService.getEpackEolTestDates({}).subscribe(
      (res: { success: boolean; result: string[] }) => {
        if (res.success) {
          this.datesToHighlight = res.result;
          this.isTestDatesReceived = true;
        }
      },
      (error) => {
        console.log("error getting epack eol test dates ");
      }
    );
  }

  getEpackEolTestsByDate(selectedDate: Date) {
    const stringDate = moment(selectedDate).format("YYYY-MM-DD");
    this.eventService.getEpackEolTestsByDate({ date: stringDate }).subscribe(
      (res: { success: boolean; result: { test_id: string }[] }) => {
        if (res.success) {
          if (res.result.length > 0) {
            this.testslist = res.result.map((test) => test.test_id);
          }

          // else {
          // no tests found means no data is present
          // either warning can be shown or chart can be plotted with empty data
          // this.plotChart({
          //   g1: [],
          //   g2: [],
          //   g3: [],
          // });
          // }
        }
      },
      (error) => {
        console.log("error getting epack eol test dates");
      }
    );
  }

  getEpackEolRawParams() {
    const payload: {
      testId: string;
      month: number;
    } = {
      testId: this.selectedTest,
      month: this.selectedDate.getMonth() + 1,
    };

    this.masterParametersList = [];

    this.eventService.getEpackEolRawParams(payload).subscribe(
      (res: {
        success: boolean;
        result: string[];
        batteryType: "piccolo" | "tg";
      }) => {
        if (res.success) {
          this.masterParametersList = res.result;
          this.batteryType = res.batteryType;

          this.resetParametersList();
          this.redLineParameter = this.redLineParameterList[1];
          this.getEpackEolRawData();
        }
      },
      (err) => {
        console.log("error getting epack eol raw data");
      }
    );
  }

  resetParametersList() {
    this.redLineParameterList = this.masterParametersList;
    this.blueLineParameterList = this.masterParametersList;
    this.greenLineParameterList = this.masterParametersList;
    this.redLineParameter = "";
    this.blueLineParameter = "";
    this.greenLineParameter = "";
  }

  renderAnalyticsGrpah(testId: string, testDate: string) {
    const splitedDate = moment(testDate).format("DD-MM-YYYY").split("-");

    const year = splitedDate[2];
    const month = splitedDate[1];
    const date = splitedDate[0];
    const fullDate = new Date(`${year}-${month}-${date}`);

    localStorage.setItem(
      "analyticsRawDataInfo",
      JSON.stringify({
        testId,
        year,
        month,
        date,
        fullDate,
      })
    );

    this.router.navigate(["eol/graph"]);
  }
}
